import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type Settings, DEFAULT_SETTINGS } from "../../types/settings";
import { mergeStoredSettings } from "../../utils/settingsPersistence";

interface SettingsState {
  settings: Settings;
  isDialogOpen: boolean;
}

const STORAGE_KEY = "dashboard_settings_v1";

const canLoadStoredSettings = (version: number | undefined) =>
  version === undefined || version <= DEFAULT_SETTINGS.version;

// Load initial settings from localStorage
const loadInitialSettings = (): Settings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<Settings>;
      if (canLoadStoredSettings(parsed.version)) {
        return mergeStoredSettings(parsed);
      }
    }
  } catch (error) {
    console.error("Failed to load settings from localStorage:", error);
  }
  return DEFAULT_SETTINGS;
};

const initialState: SettingsState = {
  settings: loadInitialSettings(),
  isDialogOpen: false,
};

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    updateSettings: (state, action: PayloadAction<Settings>) => {
      state.settings = action.payload;
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(action.payload));
      } catch (error) {
        console.error("Failed to save settings to localStorage:", error);
      }
    },
    resetSettings: (state) => {
      state.settings = DEFAULT_SETTINGS;
      // Clear localStorage
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.error("Failed to clear settings from localStorage:", error);
      }
    },
    openSettingsDialog: (state) => {
      state.isDialogOpen = true;
    },
    closeSettingsDialog: (state) => {
      state.isDialogOpen = false;
    },
  },
});

export const {
  updateSettings,
  resetSettings,
  openSettingsDialog,
  closeSettingsDialog,
} = settingsSlice.actions;

// Selectors
export const selectSettings = (state: { settings: SettingsState }) =>
  state.settings.settings;
export const selectIsDialogOpen = (state: { settings: SettingsState }) =>
  state.settings.isDialogOpen;

export default settingsSlice.reducer;
